import { CloseOutlineIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useRef } from 'react';
import { useBodyLock } from '../../utils';
import { TextSubHeader } from '../Typography';
import { Portal, canUseDOM } from './Portal';

export interface ModalProps {
  onClose?: () => void;
  open?: boolean;
  locked?: boolean;
  children: React.ReactNode;
  title?: string | React.ReactNode;
  containerClass?: string;
  removeCloseIcon?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  noPadding?: boolean;
  noBackground?: boolean;
  /** use absolute positioning for close button (X) */
  floatingCloseButton?: boolean;
  closeOnlyFromCloseIcon?: boolean;
  zIndex?: number;
}

export const Modal = ({
  removeCloseIcon,
  onClose,
  open,
  locked = false,
  children,
  title,
  containerClass,
  anchorRef,
  noPadding,
  noBackground,
  zIndex,
  floatingCloseButton = false,
  closeOnlyFromCloseIcon = false,
}: ModalProps) => {
  const overlayRef = useRef(null);
  useBodyLock(open);

  function styleCloseButton() {
    if (floatingCloseButton) {
      return 'absolute right-5 top-5 z-1000';
    }
    if (noPadding) {
      return 'pt-5 pr-5';
    }
    return '';
  }

  return (
    <AnimatePresence>
      {open && canUseDOM && (
        <Portal
          anchorRef={anchorRef}
          zIndex={zIndex}
        >
          <motion.div
            ref={overlayRef}
            key="modal-overlay"
            transition={{ duration: 0.2 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={classNames(
              'backdrop-filter absolute inset-0 w-full h-full grid items-center justify-center',
              noBackground ? 'bg-none' : 'bg-black/50'
            )}
            onClick={(event) => {
              if (
                !closeOnlyFromCloseIcon &&
                !locked &&
                event.target === overlayRef.current
              ) {
                onClose?.();
              }
            }}
          >
            <div
              className={classNames(
                'bg-white shadow-ds-lg rounded-md relative',
                {
                  'p-5': !noPadding,
                },
                containerClass
              )}
            >
              <div className="flex justify-between">
                <div className="flex content-center">
                  {typeof title === 'string' ? (
                    <TextSubHeader
                      color="text-ds-natural-700"
                      label={title}
                    />
                  ) : (
                    title
                  )}
                </div>
                {(closeOnlyFromCloseIcon || (!removeCloseIcon && !locked)) && (
                  <div
                    onClick={onClose}
                    className={`cursor-pointer ${styleCloseButton()}`}
                  >
                    <CloseOutlineIcon size={20} />
                  </div>
                )}
              </div>
              <div>{children}</div>
            </div>
          </motion.div>
        </Portal>
      )}
    </AnimatePresence>
  );
};
